import { useRef, useState } from 'react';
import type { AccountTaskActionResponse } from '@shared/types/task-actions';
import type { ThreadActionProposalSummary, ThreadTopicSummary } from '@shared/types/thread-work';
import { Button } from '@/components/ui';
import { invokeTaskAction } from '@/lib/task-action';
import { DENSE_ACTION_CLASS } from '@/components/work/WorkActionControl';
import { TaskActionQuestion } from '@/components/work/TaskActionQuestion';
import { useKeyShortcut } from '@/lib/use-key-shortcut';
import { cn } from '@/lib/utils';

/** Runs a read-only recipe or capability proposed in an app's own work. The
 * server revalidates proposal, topic version and app ownership before running;
 * the panel keeps the result since no account page holds it. */
export const AppTaskProposal = ({ topic, proposal, onDone, label, dense = false, className, hotkey }: {
  topic: ThreadTopicSummary;
  proposal: ThreadActionProposalSummary;
  onDone: (result: AccountTaskActionResponse) => void;
  /** Short verb for dense lists; the proposal's label stays as the tooltip. */
  label?: string;
  dense?: boolean;
  className?: string;
  /** In a séance, the key that launches it. */
  hotkey?: string;
}) => {
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<AccountTaskActionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pendingRef = useRef(false);

  const run = async (clarification?: { key: string; value: string }) => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPending(true); setError(null); setResult(null);
    try {
      const data = await invokeTaskAction({
        operation: 'execute_proposal', proposalId: proposal.id,
        topicId: topic.id, expectedVersion: topic.version, clarification,
      }) as AccountTaskActionResponse;
      setResult(data);
      onDone(data);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Ce travail n’a pas abouti.');
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };

  useKeyShortcut(hotkey ?? '', () => void run(), Boolean(hotkey));

  return <div className={className ?? (dense ? 'space-y-1' : 'mt-3 space-y-2')}>
    <Button
      onClick={() => void run()}
      disabled={pending}
      variant={dense ? 'secondary' : 'primary'}
      title={label ? proposal.ctaLabel : hotkey ? `Raccourci : ${hotkey.toUpperCase()}` : undefined}
      className={dense ? cn('h-auto min-h-8 max-w-full px-3 py-1 text-xs leading-4 shadow-none', DENSE_ACTION_CLASS) : undefined}
    >
      {pending ? 'Domino travaille…' : label ?? proposal.ctaLabel}
    </Button>
    {result?.status === 'needs_input'
      ? <TaskActionQuestion question={result.question} onAnswer={(answer) => void run(answer)} />
      : result && result.status !== 'completed' && <p role="status" className="text-sm text-domino-inkSoft">{result.message}</p>}
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
  </div>;
};
